/**
 * Home split-view state.
 *
 * `/` shows My Apps and Discover side by side. Either pane can take the whole
 * screen, and an explicit choice lives in the URL
 * (`?view=my-apps` / `?view=discover` / `?view=split`) — the same
 * screen-level-query-param pattern `/studio/capsules/<id>?step=…` already
 * uses. The route string itself never carries it (see
 * `routeFromPathAndSearch`), so no route comparison or chrome mapping changes.
 *
 * The former `/apps` and `/apps/discover` paths are retired; the `view` query
 * on `/` is the only way to select a pane.
 */
export type HomeView = "split" | "my-apps" | "discover";

const VIEW_VALUES: readonly string[] = ["split", "my-apps", "discover"];
export const HOME_VIEW_STORAGE_KEY = "ato.homeView.v1";

/** Device-local preference; explicit URLs continue to choose their own pane. */
export function readHomeView(): HomeView {
  try {
    const view = localStorage.getItem(HOME_VIEW_STORAGE_KEY);
    return view !== null && VIEW_VALUES.includes(view) ? view as HomeView : "split";
  } catch {
    return "split";
  }
}

export function writeHomeView(view: HomeView): void {
  try {
    localStorage.setItem(HOME_VIEW_STORAGE_KEY, view);
  } catch {
    // Navigation still works when storage is unavailable.
  }
}

/** The `view` query value, or `"split"` when absent or unrecognized. */
export function parseHomeView(search: string | null | undefined): HomeView {
  if (!search) return "split";
  const params = new URLSearchParams(
    search.startsWith("?") ? search : `?${search}`,
  );
  const view = params.get("view");
  return view !== null && VIEW_VALUES.includes(view)
    ? (view as HomeView)
    : "split";
}

/**
 * The view for a freshly loaded (or popped-to) URL. Only the query decides —
 * Discover is never selected by a path any more.
 */
export function resolveHomeView(
  _pathname: string,
  search: string | null | undefined,
): HomeView {
  return parseHomeView(search);
}

/** Explicit targets override the saved preference, including a return to split. */
export function homeViewTarget(view: HomeView): string {
  return `/?view=${view}`;
}

/** My Apps' share of the split row, in percent. Draggable; persisted. */
export const DEFAULT_SPLIT_RATIO = 65;
export const MIN_SPLIT_RATIO = 20;
export const MAX_SPLIT_RATIO = 75;
export const SPLIT_RATIO_STORAGE_KEY = "ato.homeSplitRatio";

export function clampSplitRatio(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SPLIT_RATIO;
  return Math.min(MAX_SPLIT_RATIO, Math.max(MIN_SPLIT_RATIO, value));
}

export function readSplitRatio(): number {
  try {
    const raw = localStorage.getItem(SPLIT_RATIO_STORAGE_KEY);
    if (raw === null) return DEFAULT_SPLIT_RATIO;
    return clampSplitRatio(Number(raw));
  } catch {
    return DEFAULT_SPLIT_RATIO;
  }
}

export function writeSplitRatio(value: number): void {
  try {
    localStorage.setItem(SPLIT_RATIO_STORAGE_KEY, String(clampSplitRatio(value)));
  } catch {
    // Best-effort persistence only.
  }
}

/**
 * The ratio a pointer drag lands on: the starting ratio plus the pointer's
 * travel as a share of the row width. Pure so the mapping is unit-testable;
 * the component only feeds it client coordinates.
 */
export function ratioFromDrag(
  startRatio: number,
  startClientX: number,
  clientX: number,
  containerWidth: number,
): number {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
    return clampSplitRatio(startRatio);
  }
  return clampSplitRatio(
    startRatio + ((clientX - startClientX) / containerWidth) * 100,
  );
}
